import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
    Users, Plus, Bell, AlertTriangle, TrendingUp, Calendar,
    Smile, Clock, BarChart3, ChevronRight, Shield, Check,
    X, Eye, RefreshCw, Settings, LogOut, Home, UserPlus,
    Mail, Copy, UserCheck, Trash2, FileDown
} from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ParentDashboardPage = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [children, setChildren] = useState([]);
    const [pendingInvites, setPendingInvites] = useState([]);
    const [subscription, setSubscription] = useState({});
    const [alerts, setAlerts] = useState([]);
    const [selectedChild, setSelectedChild] = useState(null);
    const [childDashboard, setChildDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAddChild, setShowAddChild] = useState(false);
    const [newChildName, setNewChildName] = useState('');
    const [addingChild, setAddingChild] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [joinCode, setJoinCode] = useState(null);
    const [generatingCode, setGeneratingCode] = useState(false);
    const [reminders, setReminders] = useState([]);
    const [showAddReminder, setShowAddReminder] = useState(false);
    const [newReminder, setNewReminder] = useState({ child_id: '', reminder_time: '18:00', days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] });
    const [reportSettings, setReportSettings] = useState({ weekly_report: true, monthly_report: true });
    const [sendingReport, setSendingReport] = useState(false);

    const token = localStorage.getItem('token');
    const api = axios.create({
        baseURL: `${BACKEND_URL}/api`,
        headers: { Authorization: `Bearer ${token}` }
    });

    useEffect(() => {
        if (!token) {
            navigate('/login');
            return;
        }
        fetchData();
        fetchReminders();
        fetchReportSettings();
    }, []);

    const fetchData = async () => {
        try {
            const [userRes, childrenRes, alertsRes] = await Promise.all([
                api.get('/auth/me'),
                api.get('/family/children'),
                api.get('/family/alerts?is_read=false')
            ]);

            setUser(userRes.data);
            setChildren(childrenRes.data.children || []);
            setPendingInvites(childrenRes.data.pending_invites || []);
            setSubscription(childrenRes.data.subscription || {});
            setAlerts(alertsRes.data.alerts || []);

            // Auto-select first child if available
            if (childrenRes.data.children?.length > 0 && !selectedChild) {
                handleSelectChild(childrenRes.data.children[0].id);
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
            if (error.response?.status === 401 || error.response?.status === 403) {
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSelectChild = async (childId) => {
        setSelectedChild(childId);
        try {
            const response = await api.get(`/family/child/${childId}/dashboard`);
            setChildDashboard(response.data);
        } catch (error) {
            console.error('Failed to fetch child dashboard:', error);
            toast.error('Failed to load child data');
        }
    };

    const handleAddChild = async (e) => {
        e.preventDefault();
        if (!newChildName.trim()) return;

        setAddingChild(true);
        try {
            const response = await api.post('/family/create-child-profile', {
                name: newChildName
            });
            toast.success(`Invite code for ${newChildName}: ${response.data.invite_code}`);
            setShowAddChild(false);
            setNewChildName('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create child profile');
        } finally {
            setAddingChild(false);
        }
    };

    const handleMarkAlertRead = async (alertId) => {
        try {
            await api.put(`/family/alerts/${alertId}/read`);
            setAlerts(alerts.filter(a => a.id !== alertId));
            toast.success('Alert marked as read');
        } catch (error) {
            toast.error('Failed to mark alert as read');
        }
    };

    const fetchReminders = async () => {
        try {
            const response = await api.get('/family/reminders');
            setReminders(response.data.reminders || []);
        } catch (error) {
            console.error('Failed to fetch reminders:', error);
        }
    };

    const fetchReportSettings = async () => {
        try {
            const response = await api.get('/family/report-settings');
            setReportSettings(response.data);
        } catch (error) {
            console.error('Failed to fetch report settings:', error);
        }
    };

    const handleGenerateJoinCode = async () => {
        setGeneratingCode(true);
        try {
            const response = await api.post('/family/generate-join-code');
            setJoinCode(response.data.join_code);
            toast.success('Join code generated! Share it with the second parent.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate join code');
        } finally {
            setGeneratingCode(false);
        }
    };

    const copyJoinCode = () => {
        navigator.clipboard.writeText(joinCode);
        toast.success('Join code copied to clipboard!');
    };

    const handleSendReport = async (period) => {
        setSendingReport(true);
        try {
            await api.post(`/family/send-report?period=${period}`);
            toast.success(`${period === 'weekly' ? 'Weekly' : 'Monthly'} report sent to your email!`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send report');
        } finally {
            setSendingReport(false);
        }
    };

    const handleUpdateReportSettings = async (updates) => {
        const newSettings = { ...reportSettings, ...updates };
        setReportSettings(newSettings);
        try {
            await api.put('/family/report-settings', newSettings);
            toast.success('Report settings updated');
        } catch (error) {
            toast.error('Failed to update settings');
        }
    };

    const handleCreateReminder = async (e) => {
        e.preventDefault();
        if (!newReminder.child_id) {
            toast.error('Please select a child');
            return;
        }
        try {
            await api.post('/family/reminders', newReminder);
            toast.success('Reminder created!');
            setShowAddReminder(false);
            setNewReminder({ child_id: '', reminder_time: '18:00', days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] });
            fetchReminders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create reminder');
        }
    };

    const handleDeleteReminder = async (reminderId) => {
        try {
            await api.delete(`/family/reminders/${reminderId}`);
            setReminders(reminders.filter(r => r.id !== reminderId));
            toast.success('Reminder deleted');
        } catch (error) {
            toast.error('Failed to delete reminder');
        }
    };

    const handleDownloadPDF = async (childId, period = 'monthly') => {
        try {
            const response = await api.get(`/family/child/${childId}/report/pdf?period=${period}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `mood_report_${period}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('PDF downloaded!');
        } catch (error) {
            toast.error('Failed to download PDF');
        }
    };

    const handleToggleReminder = async (reminderId) => {
        try {
            const response = await api.put(`/family/reminders/${reminderId}/toggle`);
            setReminders(reminders.map(r => 
                r.id === reminderId ? { ...r, is_active: response.data.is_active } : r
            ));
        } catch (error) {
            toast.error('Failed to toggle reminder');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    const severityColors = {
        warning: 'bg-amber-500/20 text-amber-500 border-amber-500/30',
        urgent: 'bg-orange-500/20 text-orange-500 border-orange-500/30',
        critical: 'bg-red-500/20 text-red-500 border-red-500/30'
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <div className="noise-overlay" />
            
            {/* Header */}
            <header className="bg-card/80 backdrop-blur-lg border-b border-border sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/icon-branded.png" alt="MoodTrack" className="w-8 h-8 rounded-lg" />
                        <div>
                            <h1 className="font-heading text-lg font-semibold">Family Dashboard</h1>
                            <p className="text-xs text-muted-foreground">Welcome, {user?.name}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => setShowSettings(!showSettings)} data-testid="settings-btn">
                            <Settings className="w-5 h-5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="relative" data-testid="alerts-btn">
                            <Bell className="w-5 h-5" />
                            {alerts.length > 0 && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                                    {alerts.length}
                                </span>
                            )}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                            <LogOut className="w-5 h-5" />
                        </Button>
                    </div>
                </div>
            </header>

            {/* Settings Panel */}
            {showSettings && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="bg-card border-b border-border"
                >
                    <div className="max-w-7xl mx-auto px-4 py-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Email Reports */}
                            <Card className="glass-card">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Mail className="w-4 h-4" />
                                        Email Reports
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-sm">Weekly Report</Label>
                                        <Checkbox 
                                            checked={reportSettings.weekly_report}
                                            onCheckedChange={(checked) => handleUpdateReportSettings({ weekly_report: checked })}
                                        />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <Label className="text-sm">Monthly Report</Label>
                                        <Checkbox 
                                            checked={reportSettings.monthly_report}
                                            onCheckedChange={(checked) => handleUpdateReportSettings({ monthly_report: checked })}
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            onClick={() => handleSendReport('weekly')}
                                            disabled={sendingReport}
                                            className="flex-1"
                                        >
                                            Send Weekly
                                        </Button>
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            onClick={() => handleSendReport('monthly')}
                                            disabled={sendingReport}
                                            className="flex-1"
                                        >
                                            Send Monthly
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Second Parent */}
                            <Card className="glass-card">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <UserCheck className="w-4 h-4" />
                                        Second Parent
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <p className="text-xs text-muted-foreground">
                                        Invite another parent to access the family dashboard (Family Complete only)
                                    </p>
                                    {joinCode ? (
                                        <div className="flex items-center gap-2">
                                            <Input value={joinCode} readOnly className="font-mono text-center" />
                                            <Button variant="outline" size="icon" onClick={copyJoinCode}>
                                                <Copy className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button 
                                            variant="outline" 
                                            onClick={handleGenerateJoinCode}
                                            disabled={generatingCode}
                                            className="w-full"
                                        >
                                            {generatingCode ? 'Generating...' : 'Generate Join Code'}
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Quick Actions */}
                            <Card className="glass-card">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        Reminders
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <p className="text-xs text-muted-foreground">
                                        Set mood check-in reminders for your children
                                    </p>
                                    <Button 
                                        variant="outline" 
                                        onClick={() => setShowAddReminder(true)}
                                        className="w-full"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Add Reminder
                                    </Button>
                                    {reminders.length > 0 && (
                                        <div className="text-xs text-muted-foreground">
                                            {reminders.length} active reminder{reminders.length > 1 ? 's' : ''}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Add Reminder Modal */}
            {showAddReminder && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <Card className="w-full max-w-md">
                        <CardHeader>
                            <CardTitle>Create Mood Reminder</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleCreateReminder} className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Select Child</Label>
                                    <select
                                        value={newReminder.child_id}
                                        onChange={(e) => setNewReminder({...newReminder, child_id: e.target.value})}
                                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                                        required
                                    >
                                        <option value="">Choose a child...</option>
                                        {children.map(child => (
                                            <option key={child.id} value={child.id}>{child.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Time</Label>
                                    <Input 
                                        type="time" 
                                        value={newReminder.reminder_time}
                                        onChange={(e) => setNewReminder({...newReminder, reminder_time: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Days</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                                            <button
                                                key={day}
                                                type="button"
                                                onClick={() => {
                                                    const days = newReminder.days.includes(day)
                                                        ? newReminder.days.filter(d => d !== day)
                                                        : [...newReminder.days, day];
                                                    setNewReminder({...newReminder, days});
                                                }}
                                                className={`px-3 py-1 rounded-full text-xs ${
                                                    newReminder.days.includes(day)
                                                        ? 'bg-primary text-primary-foreground'
                                                        : 'bg-muted'
                                                }`}
                                            >
                                                {day.slice(0, 3)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button type="button" variant="outline" onClick={() => setShowAddReminder(false)} className="flex-1">
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="flex-1">
                                        Create Reminder
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Safety Alerts Banner */}
                {alerts.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6"
                    >
                        <Card className="border-red-500/30 bg-red-500/5">
                            <CardHeader className="pb-2">
                                <CardTitle className="flex items-center gap-2 text-red-500">
                                    <AlertTriangle className="w-5 h-5" />
                                    Safety Alerts ({alerts.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {alerts.slice(0, 3).map((alert) => (
                                    <div 
                                        key={alert.id}
                                        className={`p-4 rounded-xl border ${severityColors[alert.severity]}`}
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs font-medium uppercase">{alert.severity}</span>
                                                    <span className="text-xs text-muted-foreground">
                                                        {new Date(alert.created_at).toLocaleString()}
                                                    </span>
                                                </div>
                                                <p className="font-medium">{alert.child_name}</p>
                                                <p className="text-sm text-muted-foreground">{alert.message}</p>
                                            </div>
                                            <Button 
                                                variant="ghost" 
                                                size="icon"
                                                onClick={() => handleMarkAlertRead(alert.id)}
                                                data-testid={`mark-read-${alert.id}`}
                                            >
                                                <Check className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                                {alerts.length > 3 && (
                                    <Button variant="ghost" className="w-full">
                                        View all {alerts.length} alerts
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Children Sidebar */}
                    <div className="lg:col-span-1">
                        <Card className="glass-card">
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-lg">Children</CardTitle>
                                    <Button 
                                        variant="ghost" 
                                        size="icon"
                                        onClick={() => setShowAddChild(true)}
                                        data-testid="add-child-btn"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {subscription.current_count || 0} of {subscription.max_children === -1 ? '∞' : subscription.max_children} children
                                </p>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {children.map((child) => (
                                    <button
                                        key={child.id}
                                        onClick={() => handleSelectChild(child.id)}
                                        data-testid={`child-${child.id}`}
                                        className={`w-full p-3 rounded-xl text-left transition-all ${
                                            selectedChild === child.id 
                                                ? 'bg-primary text-primary-foreground' 
                                                : 'bg-muted/30 hover:bg-muted/50'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium">{child.name}</p>
                                                <p className={`text-xs ${selectedChild === child.id ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                                    Age {child.age}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {child.current_emoji && (
                                                    <span className="text-xl">{child.current_emoji}</span>
                                                )}
                                                {child.unread_alerts > 0 && (
                                                    <span className="w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                                                        {child.unread_alerts}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                ))}

                                {/* Pending Invites */}
                                {pendingInvites.map((invite) => (
                                    <div key={invite.invite_code} className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium">{invite.name}</p>
                                                <p className="text-xs text-muted-foreground">Pending</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-mono text-sm font-medium">{invite.invite_code}</p>
                                                <p className="text-xs text-muted-foreground">Invite Code</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {children.length === 0 && pendingInvites.length === 0 && (
                                    <div className="text-center py-8">
                                        <UserPlus className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                                        <p className="text-sm text-muted-foreground">No children added yet</p>
                                        <Button 
                                            className="mt-4"
                                            onClick={() => setShowAddChild(true)}
                                        >
                                            Add Your First Child
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Add Child Modal */}
                        {showAddChild && (
                            <Card className="glass-card mt-4">
                                <CardHeader>
                                    <CardTitle className="text-lg">Add Child</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleAddChild} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Child&apos;s Name</Label>
                                            <Input
                                                data-testid="new-child-name"
                                                placeholder="Enter name"
                                                value={newChildName}
                                                onChange={(e) => setNewChildName(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <Button 
                                                type="button" 
                                                variant="outline" 
                                                onClick={() => setShowAddChild(false)}
                                                className="flex-1"
                                            >
                                                Cancel
                                            </Button>
                                            <Button 
                                                type="submit" 
                                                className="flex-1"
                                                disabled={addingChild}
                                                data-testid="create-child-btn"
                                            >
                                                {addingChild ? 'Creating...' : 'Create'}
                                            </Button>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Main Dashboard */}
                    <div className="lg:col-span-3">
                        {childDashboard ? (
                            <div className="space-y-6">
                                {/* Child Header */}
                                <Card className="glass-card">
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-3xl">
                                                    {childDashboard.current_emoji || '😊'}
                                                </div>
                                                <div>
                                                    <h2 className="font-heading text-2xl font-semibold">{childDashboard.child_name}</h2>
                                                    <p className="text-muted-foreground">Age {childDashboard.age}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="text-right">
                                                    <p className="text-sm text-muted-foreground">Current Mood</p>
                                                    <p className="font-heading text-3xl font-bold text-primary">
                                                        {childDashboard.current_mood || '—'}/10
                                                    </p>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleDownloadPDF(selectedChild, 'monthly')}
                                                    data-testid="download-pdf-btn"
                                                >
                                                    <FileDown className="w-4 h-4 mr-2" />
                                                    PDF Report
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Stats Grid */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <Card className="glass-card">
                                        <CardContent className="p-4">
                                            <TrendingUp className="w-5 h-5 text-chart-1 mb-2" />
                                            <p className="text-2xl font-bold">{childDashboard.analysis?.average_mood_30d || '—'}</p>
                                            <p className="text-xs text-muted-foreground">Avg Mood (30d)</p>
                                        </CardContent>
                                    </Card>
                                    <Card className="glass-card">
                                        <CardContent className="p-4">
                                            <Calendar className="w-5 h-5 text-chart-2 mb-2" />
                                            <p className="text-2xl font-bold">{childDashboard.mood_streak}</p>
                                            <p className="text-xs text-muted-foreground">Day Streak</p>
                                        </CardContent>
                                    </Card>
                                    <Card className="glass-card">
                                        <CardContent className="p-4">
                                            <BarChart3 className="w-5 h-5 text-chart-3 mb-2" />
                                            <p className="text-2xl font-bold">{childDashboard.analysis?.total_entries_30d || 0}</p>
                                            <p className="text-xs text-muted-foreground">Entries (30d)</p>
                                        </CardContent>
                                    </Card>
                                    <Card className="glass-card">
                                        <CardContent className="p-4">
                                            <Shield className="w-5 h-5 text-chart-4 mb-2" />
                                            <p className="text-2xl font-bold capitalize">{childDashboard.analysis?.mood_stability || '—'}</p>
                                            <p className="text-xs text-muted-foreground">Stability</p>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Mood Trends Chart */}
                                {childDashboard.mood_trends?.length > 0 && (
                                    <Card className="glass-card">
                                        <CardHeader>
                                            <CardTitle>Mood Trends (30 Days)</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="h-64">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={childDashboard.mood_trends}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                                        <XAxis 
                                                            dataKey="date" 
                                                            tick={{ fontSize: 12 }}
                                                            stroke="hsl(var(--muted-foreground))"
                                                        />
                                                        <YAxis 
                                                            domain={[0, 10]} 
                                                            tick={{ fontSize: 12 }}
                                                            stroke="hsl(var(--muted-foreground))"
                                                        />
                                                        <Tooltip 
                                                            contentStyle={{ 
                                                                background: 'hsl(var(--card))', 
                                                                border: '1px solid hsl(var(--border))',
                                                                borderRadius: '8px'
                                                            }}
                                                        />
                                                        <Line 
                                                            type="monotone" 
                                                            dataKey="mood" 
                                                            stroke="hsl(var(--primary))" 
                                                            strokeWidth={2}
                                                            dot={{ fill: 'hsl(var(--primary))' }}
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Bottom Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Top Emojis */}
                                    <Card className="glass-card">
                                        <CardHeader>
                                            <CardTitle className="flex items-center gap-2">
                                                <Smile className="w-5 h-5" />
                                                Top Emojis
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="flex gap-4 text-4xl">
                                                {Object.entries(childDashboard.top_emojis || {}).slice(0, 5).map(([emoji, count]) => (
                                                    <div key={emoji} className="text-center">
                                                        <span>{emoji}</span>
                                                        <p className="text-xs text-muted-foreground mt-1">{count}x</p>
                                                    </div>
                                                ))}
                                                {Object.keys(childDashboard.top_emojis || {}).length === 0 && (
                                                    <p className="text-sm text-muted-foreground">No data yet</p>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* Time of Day */}
                                    <Card className="glass-card">
                                        <CardHeader>
                                            <CardTitle className="flex items-center gap-2">
                                                <Clock className="w-5 h-5" />
                                                Time of Day Insights
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2">
                                                {Object.entries(childDashboard.time_of_day_insights || {}).map(([time, data]) => (
                                                    <div key={time} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                                                        <span className="text-sm">{time}</span>
                                                        <div className="text-right">
                                                            <span className="font-medium">{data.avg_mood}/10</span>
                                                            <span className="text-xs text-muted-foreground ml-2">({data.entries} entries)</span>
                                                        </div>
                                                    </div>
                                                ))}
                                                {Object.keys(childDashboard.time_of_day_insights || {}).length === 0 && (
                                                    <p className="text-sm text-muted-foreground">No data yet</p>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Partner Requests */}
                                {childDashboard.pending_partner_requests > 0 && (
                                    <Card className="glass-card border-amber-500/30">
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <Users className="w-5 h-5 text-amber-500" />
                                                    <div>
                                                        <p className="font-medium">Partner Requests</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {childDashboard.pending_partner_requests} pending approval
                                                        </p>
                                                    </div>
                                                </div>
                                                <Link to={`/family/child/${selectedChild}/partners`}>
                                                    <Button variant="outline" size="sm">
                                                        Review
                                                        <ChevronRight className="w-4 h-4 ml-1" />
                                                    </Button>
                                                </Link>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        ) : (
                            <Card className="glass-card h-full flex items-center justify-center">
                                <CardContent className="text-center py-16">
                                    <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                                    <h3 className="font-heading text-xl font-medium mb-2">Select a Child</h3>
                                    <p className="text-muted-foreground">
                                        Choose a child from the sidebar to view their mood dashboard
                                    </p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ParentDashboardPage;
